<template>
  <div class="pdf-previewer">
    <div class="instructions-container">
      <p>{{ $t('pdfPreviewer.instructions') }}</p>
    </div>
    <div class="actions-button">
      <div class="confirm-button-container">
        <el-button
          type="primary"
          class="confirm-button"
          :disabled="!Object.keys(this.position).length"
          @click="confirmSignArea"
        >
          {{ $t('pdfPreviewer.confirmButton') }}
        </el-button>
      </div>
      <div class="page-buttons">
        <el-button
          type="primary"
          class="page-button"
          icon="el-icon-arrow-left"
          circle
          :disabled="prevBtnDisabled"
          @click="onPrevPage"
        >
        </el-button>
        <span class="pages-count">{{ page }} / {{ pageCount }}</span>
        <el-button
          type="primary"
          class="page-button"
          icon="el-icon-arrow-right"
          circle
          :disabled="nextBtnDisabled"
          @click="onNextPage"
        >
        </el-button>
      </div>
    </div>
    <VuePdfEmbed
      ref="pdfRef"
      :width="width"
      :scale="1"
      :page="page"
      :source="source"
      :rotation="rotation"
      :disableAnnotationLayer="true"
      :disableTextLayer="true"
      @rendered="handleDocumentRender"
    />
  </div>
</template>

<script>
  import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';

  export default {
    name: 'PdfPreviewer',
    components: {
      VuePdfEmbed,
    },
    props: {
      source: { type: String, required: true },
      rotation: { type: Number, default: 0 },
    },
    data() {
      return {
        page: 1,
        pageCount: 1,
        ev: {},
        position: {},
        pageViewport: null,
        width: 612,
      };
    },
    computed: {
      prevBtnDisabled() {
        if (this.page === 1) return true;
        return false;
      },
      nextBtnDisabled() {
        if (this.page === this.pageCount) return true;
        return false;
      },
    },
    methods: {
      async handleDocumentRender() {
        // get pdf page viewport
        const page = await this.$refs.pdfRef.document.getPage(1);
        /**
         * set width as the same of pdf page with
         * because vue-pdf-embed library scale page with some variables
         */
        // eslint-disable-next-line prefer-destructuring
        this.width = page.view[2];
        this.pageViewport = await page.getViewport({
          scale: this.$refs.pdfRef.scale ?? 1,
        });
        this.pageCount = this.$refs.pdfRef.pageCount;
        // select pdf canvas and set id
        const canvas = this.$el.querySelector('canvas');
        canvas.setAttribute('id', 'pdfCanvas');
        // get canvas parent div
        const canvasContainer =
          document.getElementById('pdfCanvas').parentElement;
        // create second canvas and set id, width and height
        const canvasCover = document.createElement('canvas');
        canvasCover.setAttribute('id', 'canvasCover');
        canvasCover.width = document.getElementById('pdfCanvas').offsetWidth;
        canvasCover.height = document.getElementById('pdfCanvas').offsetHeight;
        // add style
        canvasContainer.style.position = 'relative';
        canvasCover.style.position = 'absolute';
        canvasCover.style.top = 0;
        canvasCover.style.left = 0;
        // append second canvas to pdf canvas container
        canvasContainer.appendChild(canvasCover);
        canvasCover.addEventListener('mousedown', this.evtDown, false);
      },
      onPrevPage() {
        if (this.page <= 1) {
          return;
        }
        this.page -= 1;
      },
      onNextPage() {
        if (this.page >= this.pageCount) {
          return;
        }
        this.page += 1;
      },
      confirmSignArea() {
        if (Object.keys(this.position).length) {
          this.$emit('confirm-sign-area', this.position);
        }
      },
      //----------------
      evtDown(event) {
        event.preventDefault();
        const canvas = event.target;
        const ctx = canvas.getContext('2d');
        const posxy = this.coords(event, canvas);

        this.ev = {
          ipxy: posxy,
          pxy: posxy,
          w: 0,
          h: 0,
          size: [],
          canvas: { elem: canvas, ctx },
        };

        document.addEventListener('mousemove', this.evtMove, false);
        document.addEventListener('mouseup', this.evtUp, false);

        return false;
      },
      // MOVE
      evtMove(e) {
        e.preventDefault();
        this.ev.pxy = this.coords(e, this.ev.canvas.elem);
        let { x, y } = this.ev.pxy;

        if (x < 0) x = 0;
        if (y < 0) y = 0;

        this.ev.w = Math.abs(x - this.ev.ipxy.x);
        this.ev.h = Math.abs(y - this.ev.ipxy.y);

        this.ev.size = this.draw(this.ev);

        return false;
      },
      // UP
      evtUp(e) {
        e.preventDefault();

        if (this.ev.w > 0 && this.ev.h > 0) {
          // gestione rettangolo
          this.position = {
            x: this.ev.size[0],
            y: this.ev.size[1],
            width: this.ev.size[2],
            height: this.ev.size[3],
            page: this.page,
          };
          // calc pdf coords
          const canvasCoords = this.position;
          const pdfTopLeft = this.pageViewport.convertToPdfPoint(
            canvasCoords.x,
            canvasCoords.y
          );
          const pdfBottomRight = this.pageViewport.convertToPdfPoint(
            canvasCoords.width + canvasCoords.x,
            canvasCoords.height + canvasCoords.y
          );

          const pdfCoords = {
            x: pdfTopLeft[0],
            y: pdfTopLeft[1],
            width: pdfBottomRight[0] - pdfTopLeft[0],
            height: pdfTopLeft[1] - pdfBottomRight[1],
            page: this.page,
          };
          this.$emit('create-sign-area', pdfCoords);
        }

        document.removeEventListener('mousemove', this.evtMove, false);
        document.removeEventListener('mouseup', this.evtUp, false);

        return false;
      },
      draw(ev) {
        let x;
        let y;
        let w;
        let h;
        const { ctx } = ev.canvas;

        x = ev.ipxy.x;
        y = ev.ipxy.y;
        w = ev.w;
        h = ev.h;

        if (ev.pxy.x < x) x = ev.pxy.x;
        if (ev.pxy.y < y) y = ev.pxy.y;
        if (x + w > document.getElementById('canvasCover').offsetWidth)
          w = document.getElementById('canvasCover').offsetWidth - x;
        if (y + h > document.getElementById('canvasCover').offsetHeight)
          h = document.getElementById('canvasCover').offsetHeight - y;
        if (x < 0) x = 0;
        if (y < 0) y = 0;

        ctx.clearRect(
          0,
          0,
          document.getElementById('canvasCover').offsetWidth,
          document.getElementById('canvasCover').offsetHeight
        );
        ctx.strokeColor = '#f00';
        ctx.beginPath();
        ctx.rect(x, y, w, h);
        ctx.stroke();

        return [x, y, w, h];
      },
      coords(e, elem) {
        let dxy = { l: 0, t: 0 };
        if (elem) dxy = this.ElPos(elem);
        const container = document.getElementsByClassName(
          'el-container main-container is-vertical'
        );
        return {
          x: e.pageX - dxy.l,
          y: e.pageY + container[0].scrollTop - dxy.t,
        };
      },
      ElPos(j) {
        let l = 0;
        let t = 0;
        const w = j.offsetWidth;
        const h = j.offsetHeight;
        while (j.offsetParent) {
          l += j.offsetLeft;
          t += j.offsetTop;
          // eslint-disable-next-line no-param-reassign
          j = j.offsetParent;
        }
        l += j.offsetLeft;
        t += j.offsetTop;
        return {
          l,
          t,
          w,
          h,
        };
      },
      //----------------
    },
  };
</script>

<style lang="scss">
  .pdf-previewer {
    display: flex;
    flex-direction: column;
    .instructions-container {
      margin-bottom: 1rem;
      color: $--color-white;
    }
    .actions-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      .page-buttons {
        text-align: end;
        color: $--color-white;
        .pages-count {
          margin: 0 0.5rem;
        }
      }
    }
    .vue-pdf-embed div canvas {
      border: 1px solid $--color-black;
    }
  }
</style>
